// Форматирование в стиле Telegram.
//
// Форматированный текст хранится и передаётся на сервер в «формате Telegram» -
// ограниченном HTML с тегами <b> <i> <u> <s> <code> <pre> <blockquote>
// <tg-spoiler> <a href>. Переносы строк - обычные "\n", остальное - экранированный
// текст. Сервер превращает это в отметки форматирования Telegram
// (app/services/tg_formatting.py), а входящие сообщения приходят в том же виде.
//
// Здесь - перевод между этим форматом, содержимым редактора и простым текстом.
// Разбираем строго по белому списку тегов: всё прочее становится текстом, так
// что вставить чужую разметку или скрипт через шаблон или сообщение нельзя.

export const TG_TAGS = ['b', 'i', 'u', 's', 'code', 'pre', 'blockquote', 'tg-spoiler', 'a'] as const
export type TgTag = typeof TG_TAGS[number]

const TAG_ALIASES: Record<string, TgTag> = {
    b: 'b',
    strong: 'b',
    i: 'i',
    em: 'i',
    u: 'u',
    ins: 'u',
    s: 's',
    strike: 's',
    del: 's',
    code: 'code',
    pre: 'pre',
    blockquote: 'blockquote',
    'tg-spoiler': 'tg-spoiler',
    a: 'a',
}

export const escapeHtml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const escapeAttr = (text: string): string => escapeHtml(text).replace(/"/g, '&quot;')

const isSafeUrl = (url: string) => /^(https?:|tg:)/i.test(url.trim())

// Простой текст -> формат Telegram (без форматирования).
export const plainToTg = (text: string): string => escapeHtml(text || '')

const parse = (html: string): Document =>
    new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html')

// Тег элемента в нашем формате или null, если форматированием он не является.
const tgTagOf = (element: Element): TgTag | null => {
    const tag = element.tagName.toLowerCase()
    if (tag === 'span') {
        if (element.classList.contains('tg-spoiler') || element.getAttribute('data-tg') === 'spoiler') return 'tg-spoiler'
        const weight = (element as HTMLElement).style?.fontWeight
        if (weight === 'bold' || Number(weight) >= 600) return 'b'
        if ((element as HTMLElement).style?.fontStyle === 'italic') return 'i'
        return null
    }
    return TAG_ALIASES[tag] || null
}

// Любой DOM (содержимое редактора, вставка, разобранная строка) -> формат Telegram.
export const domToTg = (root: Node): string => {
    let out = ''
    // Цитата - блок: перенос после неё ставим сами.
    let afterBlock = false

    const newline = () => {
        if (out && !out.endsWith('\n')) out += '\n'
    }

    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const value = (node.textContent || '').replace(/\u00a0/g, ' ')
            if (value) afterBlock = false
            out += escapeHtml(value)
            return
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return
        const element = node as Element
        const tag = element.tagName.toLowerCase()

        if (tag === 'br') {
            // Сразу за цитатой перенос уже стоит - второй пустой строкой не считаем.
            if (afterBlock) {
                afterBlock = false
                return
            }
            out += '\n'
            return
        }
        if (tag === 'div' || tag === 'p') {
            // Браузер оборачивает строки редактора в div: это перенос строки.
            newline()
            element.childNodes.forEach(walk)
            return
        }

        const tgTag = tgTagOf(element)
        if (!tgTag) {
            element.childNodes.forEach(walk)
            return
        }
        if (tgTag === 'blockquote') newline()
        if (tgTag === 'a') {
            const href = element.getAttribute('href') || ''
            if (!isSafeUrl(href)) {
                element.childNodes.forEach(walk)
                return
            }
            out += `<a href="${escapeAttr(href)}">`
        } else if (tgTag === 'blockquote' && element.hasAttribute('expandable')) {
            out += '<blockquote expandable>'
        } else {
            out += `<${tgTag}>`
        }
        const before = out.length
        element.childNodes.forEach(walk)
        if (tgTag === 'blockquote') {
            // Перенос в конце цитаты - это граница блока, а не её содержимое.
            while (out.length > before && out.endsWith('\n')) out = out.slice(0, -1)
        }
        out += `</${tgTag}>`
        if (tgTag === 'blockquote') {
            out += '\n'
            afterBlock = true
        }
    }

    root.childNodes.forEach(walk)

    // Пустые теги (<b></b>) и лишние переносы в конце ни на что не влияют.
    let cleaned = out
    for (let pass = 0; pass < 3; pass += 1) {
        cleaned = cleaned.replace(/<(b|i|u|s|code|pre|tg-spoiler|blockquote)(?: expandable)?><\/\1>/g, '')
    }
    return cleaned.replace(/\n+$/, '')
}

// Строка в формате Telegram -> безопасная разметка для редактора.
export const tgToEditorHtml = (tg: string): string => {
    const doc = parse(tg || '')
    const build = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
            return escapeHtml(node.textContent || '').replace(/\n/g, '<br>')
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return ''
        const element = node as Element
        const inner = Array.from(element.childNodes).map(build).join('')
        const tgTag = tgTagOf(element)
        if (!tgTag) return inner
        if (tgTag === 'tg-spoiler') return `<span class="tg-spoiler" data-tg="spoiler">${inner}</span>`
        if (tgTag === 'a') {
            const href = element.getAttribute('href') || ''
            return isSafeUrl(href) ? `<a href="${escapeAttr(href)}">${inner}</a>` : inner
        }
        return `<${tgTag}>${inner}</${tgTag}>`
    }
    return Array.from(doc.body.childNodes).map(build).join('')
}

// Формат Telegram -> простой текст (для проверок «пусто ли», превью, поиска).
export const tgToPlain = (tg: string): string => {
    if (!tg) return ''
    const doc = parse(tg)
    return (doc.body.textContent || '').replace(/ /g, ' ')
}

// Есть ли в строке форматирование. Без него сообщение отправляется как обычный текст.
export const hasTgFormatting = (tg: string): boolean => {
    if (!tg || !tg.includes('<')) return false
    const doc = parse(tg)
    return Array.from(doc.body.querySelectorAll('*')).some((element) => tgTagOf(element) !== null)
}

// Содержимое шаблона для редактора: старые шаблоны - простой текст.
export const templateToTg = (content: string, isFormatted?: boolean): string =>
    isFormatted ? content || '' : plainToTg(content || '')

// Подстановка {name}, {geo}... для предпросмотра: значения экранируются так же,
// как это делает сервер при отправке.
export const renderTgTemplate = (tg: string, values: Record<string, string>): string => {
    let result = tg || ''
    Object.entries(values).forEach(([key, value]) => {
        result = result.split(`{${key}}`).join(escapeHtml(value))
    })
    return result
}
